import type { CSSProperties } from 'react';
import type { ReservationBooking } from '../services/reservation-system/reservation-board.service';

export const RS_WEEKDAY_I18N_KEYS = [
  'reservation.board.weekdaySun',
  'reservation.board.weekdayMon',
  'reservation.board.weekdayTue',
  'reservation.board.weekdayWed',
  'reservation.board.weekdayThu',
  'reservation.board.weekdayFri',
  'reservation.board.weekdaySat',
] as const;

export const RS_MAX_RESOURCE_COUNT = 20;

export const RS_DEFAULT_BOOKING_DURATION_MS = 60 * 60 * 1000;

/** 既定の予約開始・終了を揃える分刻み（例: 11:06 → 11:30） */
export const RS_BOOKING_SLOT_MINUTES = 30;

/** 終了補正の下限（スロット単位）。−30 連打で 1 時間に跳ねないようにする */
export const RS_MIN_BOOKING_DURATION_MS = RS_BOOKING_SLOT_MINUTES * 60 * 1000;

/** 次のスロット境界へ切り上げ（ちょうど境界ならそのまま） */
export function ceilRsDateToBookingSlot(
  date: Date,
  stepMinutes: number = RS_BOOKING_SLOT_MINUTES
): Date {
  const step = Math.max(1, Math.floor(stepMinutes));
  const d = new Date(date.getTime());
  d.setSeconds(0, 0);
  const totalMinutes = d.getHours() * 60 + d.getMinutes();
  const rem = totalMinutes % step;
  if (rem === 0) return d;
  const add = step - rem;
  return new Date(d.getTime() + add * 60 * 1000);
}

export function ceilRsLocalDateTimeToBookingSlot(
  localValue: string,
  stepMinutes: number = RS_BOOKING_SLOT_MINUTES
): string {
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return localValue;
  return toRsDateTimeLocalValue(ceilRsDateToBookingSlot(d, stepMinutes));
}

export function formatRsCalendarMonthTitle(locale: string, year: number, month: number): string {
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(
    new Date(year, month - 1, 1)
  );
}

export function getDayRowBackgroundClass(
  weekday: number,
  isToday: boolean,
  isHoliday: boolean,
  styles: Record<string, string>
): string {
  if (isToday) return styles.dayRowToday;
  if (weekday === 0) return styles.dayRowSunday;
  if (weekday === 6) return styles.dayRowSaturday;
  if (isHoliday) return styles.dayRowHoliday;
  return '';
}

export function formatRsBookingTimeRange(
  startIso: string,
  endIso: string,
  rangeSeparator = ' – ',
  overnightPrefix = '翌'
): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  return formatRsLocalTimeRangeDisplay(
    toRsDateTimeLocalValue(start),
    toRsDateTimeLocalValue(end),
    rangeSeparator,
    overnightPrefix
  );
}

/** フォームの `YYYY-MM-DDTHH:mm` からカレンダー下書き用の時刻（または日またぎ）表示 */
export function formatRsLocalTimeRangeDisplay(
  startLocal: string,
  endLocal: string,
  rangeSeparator = '〜',
  overnightPrefix = '翌'
): string {
  const start = splitRsLocalDateTime(startLocal);
  const end = splitRsLocalDateTime(endLocal);
  if (!start.time || !end.time) return '';
  if (start.date && end.date && start.date !== end.date) {
    const startParts = parseRsLocalDateYmd(start.date);
    const endParts = parseRsLocalDateYmd(end.date);
    if (startParts && endParts) {
      const startUtc = Date.UTC(startParts.year, startParts.month - 1, startParts.day);
      const endUtc = Date.UTC(endParts.year, endParts.month - 1, endParts.day);
      const dayDiff = Math.round((endUtc - startUtc) / 86_400_000);
      // 翌日またぎは狭い列向けに短縮（例: 23:00〜翌00:00）
      if (dayDiff === 1) {
        return `${start.time}${rangeSeparator}${overnightPrefix}${end.time}`;
      }
    }
    return `${formatRsLocalDateDisplay(start.date)} ${start.time}${rangeSeparator}${formatRsLocalDateDisplay(end.date)} ${end.time}`;
  }
  return `${start.time}${rangeSeparator}${end.time}`;
}

/** ローカル日時がカレンダー表示年月に含まれるか */
export function isRsLocalDateTimeInMonth(
  localDateTime: string,
  year: number,
  month: number
): boolean {
  const parts = parseRsLocalDateYmd(localDateTime.slice(0, 10));
  return Boolean(parts && parts.year === year && parts.month === month);
}

/**
 * 空月案内を出すか。
 * - 確定予約がある／表示月に下書きがある → 出さない
 * - 選択が他月（外月バナーがある）→ 重ねない
 */
export function shouldShowRsEmptyBookingsHint(opts: {
  bookingGroupCount: number;
  year: number;
  month: number;
  draftStartDateTime?: string;
  draftEndDateTime?: string;
  selectedResourceNames?: string[];
}): boolean {
  if (opts.bookingGroupCount > 0) return false;
  const start = opts.draftStartDateTime ?? '';
  const end = opts.draftEndDateTime ?? '';
  const resources = opts.selectedResourceNames ?? [];
  if (start && end && resources.length > 0 && isRsLocalDateTimeInMonth(start, opts.year, opts.month)) {
    return false;
  }
  if (start && !isRsLocalDateTimeInMonth(start, opts.year, opts.month)) {
    return false;
  }
  return true;
}

type RsScrollDayOptions = {
  behavior?: ScrollBehavior;
  root?: ParentNode | null;
};

/** 日セルが視野外のとき、寄せるための縦デルタ（すでに見えていれば 0） */
export function computeRsDayScrollDelta(opts: {
  dayTop: number;
  dayBottom: number;
  viewportHeight: number;
  marginTop: number;
  marginBottom: number;
}): number {
  const { dayTop, dayBottom, viewportHeight, marginTop, marginBottom } = opts;
  if (dayTop >= marginTop && dayBottom <= viewportHeight - marginBottom) return 0;
  if (dayTop < marginTop) return dayTop - marginTop;
  return dayBottom - (viewportHeight - marginBottom);
}

/**
 * sticky サイドバーが共有バー下に留まる上限 scrollY。
 * layoutBottom は getBoundingClientRect().bottom（現在の scrollY 基準）。
 */
export function maxRsScrollTopKeepingSidebarSticky(opts: {
  scrollY: number;
  layoutBottom: number;
  shareOffset: number;
  sidebarHeight: number;
  padding?: number;
}): number {
  const pad = opts.padding ?? 8;
  return opts.scrollY + opts.layoutBottom - opts.shareOffset - opts.sidebarHeight - pad;
}

/** 予約表: サイドバー縦積み／固定登録バーと同じ狭い画面閾値 */
export const RS_NARROW_BOARD_MQ = '(max-width: 992px)';

/** 狭い画面: フォーム主視野（カレンダー畳み込み）フラグ */
export const RS_BOARD_FORM_FOCUS_ATTR = 'data-rs-form-focus';

export function isRsNarrowBoardLayout(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(RS_NARROW_BOARD_MQ).matches;
}

export function getRsBoardLayoutEl(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-rs-board-layout]');
}

export function isRsBoardFormFocused(layout?: HTMLElement | null): boolean {
  const root = layout ?? getRsBoardLayoutEl();
  return !!root?.hasAttribute(RS_BOARD_FORM_FOCUS_ATTR);
}

export function setRsBoardFormFocus(on: boolean, layout?: HTMLElement | null): void {
  const root = layout ?? getRsBoardLayoutEl();
  if (!root) return;
  if (on) {
    root.setAttribute(RS_BOARD_FORM_FOCUS_ATTR, '');
    return;
  }
  root.removeAttribute(RS_BOARD_FORM_FOCUS_ATTR);
  const active = document.activeElement;
  if (active instanceof HTMLElement && root.contains(active)) {
    active.blur();
  }
}

export function afterRsLayoutPaint(run: () => void): void {
  window.requestAnimationFrame(() => window.requestAnimationFrame(run));
}

type RevealRsBookingFormOptions = {
  focusNameInputId?: string;
  /** true のとき表示名が空の場合だけフォーカス */
  focusNameIfEmpty?: boolean;
  displayName?: string;
  behavior?: ScrollBehavior;
};

/**
 * 狭い画面で登録フォームを主視野にする（グリッド畳み＋サイドバーへ寄せる）。
 * マス選択後など「フォームで確定したい」導線用。広い画面は名前フォーカスのみ。
 */
export function revealRsBookingForm(options?: RevealRsBookingFormOptions): void {
  const behavior = options?.behavior ?? 'smooth';
  const nameId = options?.focusNameInputId;
  const shouldFocus =
    !!nameId && (!options?.focusNameIfEmpty || !(options.displayName ?? '').trim());

  if (!isRsNarrowBoardLayout()) {
    if (shouldFocus && nameId) {
      afterRsLayoutPaint(() => document.getElementById(nameId)?.focus());
    }
    return;
  }

  setRsBoardFormFocus(true);
  afterRsLayoutPaint(() => {
    document.querySelector<HTMLElement>('[data-rs-sidebar]')?.scrollIntoView({
      behavior,
      block: 'start',
    });
    if (shouldFocus && nameId) {
      document.getElementById(nameId)?.focus({ preventScroll: true });
    }
  });
}

/**
 * 登録直後など: フォーム主視野を解除し、該当日のカレンダーへ寄せる。
 * （狭い画面で「登録したのに表が見えない」を防ぐ）
 */
export function revealRsBookingCalendarDay(
  day: number,
  options?: { behavior?: ScrollBehavior }
): void {
  if (isRsNarrowBoardLayout()) {
    setRsBoardFormFocus(false);
  }
  afterRsLayoutPaint(() => {
    if (!Number.isFinite(day) || day < 1) return;
    scrollRsCalendarDayIntoView(day, { behavior: options?.behavior ?? 'smooth' });
  });
}

/** 狭い画面の固定登録バー高さ（fixed のときだけ。それ以外は 0） */
export function getRsMobileSubmitBarInsetPx(): number {
  const submitBar = document.querySelector<HTMLElement>('[data-rs-submit-bar]');
  if (!submitBar) return 0;
  if (getComputedStyle(submitBar).position !== 'fixed') return 0;
  return Math.ceil(submitBar.getBoundingClientRect().height);
}

/**
 * 要素下端が固定登録バーに隠れないよう window をずらす。
 * （details 展開やフォーム末尾のタップ不能を防ぐ）
 */
export function scrollRsElementClearOfMobileSubmitBar(
  el: HTMLElement,
  options?: { behavior?: ScrollBehavior; extraPx?: number }
): void {
  const inset = getRsMobileSubmitBarInsetPx() + (options?.extraPx ?? 12);
  if (inset <= 0) return;
  const rect = el.getBoundingClientRect();
  const limit = window.innerHeight - inset;
  if (rect.bottom <= limit && rect.top >= 0) return;
  const delta =
    rect.bottom > limit ? rect.bottom - limit : rect.top < 0 ? rect.top : 0;
  if (Math.abs(delta) < 1) return;
  window.scrollBy({ top: delta, behavior: options?.behavior ?? 'smooth' });
}

/**
 * sticky ツールバー＋項目ヘッダ分の上余白（CSS 変数の和）。
 * scrollIntoView の scroll-margin は overflow:clip 祖先などで無視されることがあるため、
 * 実スクロールはこちらを正とする。
 */
export function resolveRsCalendarStickyMarginTop(opts: {
  toolbarOffsetPx: number;
  headerOffsetPx: number;
  fallbackPx?: number;
}): number {
  const total = Math.max(0, opts.toolbarOffsetPx) + Math.max(0, opts.headerOffsetPx);
  return total > 0 ? total : (opts.fallbackPx ?? 88);
}

export function readRsCalendarStickyMarginTop(cssRoot: Element): number {
  const style = getComputedStyle(cssRoot);
  return resolveRsCalendarStickyMarginTop({
    toolbarOffsetPx: Number.parseFloat(style.getPropertyValue('--rs-cal-toolbar-offset')) || 0,
    headerOffsetPx: Number.parseFloat(style.getPropertyValue('--rs-cal-header-offset')) || 0,
  });
}

/**
 * `[data-rs-day]` へ寄せる。
 * sticky 帯下に潜らないよう CSS 変数の余白で window スクロールする。
 * デスクトップはさらにサイドバーが共有バーへ潜らないよう上限をクランプする。
 */
export function scrollRsCalendarDayIntoView(
  day: number,
  options?: RsScrollDayOptions
): boolean {
  if (!Number.isFinite(day) || day < 1) return false;
  const root = options?.root ?? document;
  const el = root.querySelector<HTMLElement>(`[data-rs-day="${day}"]`);
  if (!el) return false;

  const behavior = options?.behavior ?? 'smooth';
  const layout = el.closest<HTMLElement>('[data-rs-board-layout]');
  const cssRoot = layout ?? document.documentElement;
  const shareOffset =
    Number.parseFloat(getComputedStyle(cssRoot).getPropertyValue('--rs-share-bar-offset')) || 0;
  const marginTop = readRsCalendarStickyMarginTop(cssRoot);
  const marginBottom = isRsNarrowBoardLayout()
    ? getRsMobileSubmitBarInsetPx() + 16
    : 16;
  const rect = el.getBoundingClientRect();
  const delta = computeRsDayScrollDelta({
    dayTop: rect.top,
    dayBottom: rect.bottom,
    viewportHeight: window.innerHeight,
    marginTop,
    marginBottom,
  });
  if (delta === 0) return true;

  const next = Math.max(0, window.scrollY + delta);
  if (isRsNarrowBoardLayout()) {
    if (behavior === 'smooth') {
      window.scrollTo({ top: next, behavior });
    } else {
      const scrolling = document.scrollingElement;
      if (scrolling) scrolling.scrollTop = next;
      else window.scrollTo({ top: next, behavior: 'auto' });
    }
    return true;
  }

  const sidebar = layout?.querySelector<HTMLElement>('[data-rs-sidebar]');
  const layoutBottom = layout?.getBoundingClientRect().bottom;
  const sidebarHeight = sidebar?.getBoundingClientRect().height ?? 0;
  const maxScroll =
    layoutBottom != null && sidebarHeight > 0
      ? maxRsScrollTopKeepingSidebarSticky({
          scrollY: window.scrollY,
          layoutBottom,
          shareOffset,
          sidebarHeight,
        })
      : Number.POSITIVE_INFINITY;
  const clamped = Math.min(next, maxScroll);
  if (Math.abs(clamped - window.scrollY) < 1) return true;
  if (behavior === 'smooth') {
    window.scrollTo({ top: clamped, behavior });
  } else {
    const scrolling = document.scrollingElement;
    if (scrolling) scrolling.scrollTop = clamped;
    else window.scrollTo({ top: clamped, behavior: 'auto' });
  }
  return true;
}

/**
 * sticky / 列ヘッダ確定後に日セルへ寄せる（二重 rAF）。
 * 戻り値でキャンセルできる。
 */
export function scheduleScrollRsCalendarDayIntoView(
  day: number,
  options?: RsScrollDayOptions
): () => void {
  if (!Number.isFinite(day) || day < 1) return () => {};
  let cancelled = false;
  let innerFrame = 0;
  const outerFrame = window.requestAnimationFrame(() => {
    innerFrame = window.requestAnimationFrame(() => {
      if (cancelled) return;
      scrollRsCalendarDayIntoView(day, { behavior: 'auto', ...options });
    });
  });
  return () => {
    cancelled = true;
    window.cancelAnimationFrame(outerFrame);
    window.cancelAnimationFrame(innerFrame);
  };
}

export type RsDraftBookingCell = {
  resourceIndex: number;
  firstDay: number;
  lastDay: number;
};

/** 表示中の月に載る下書き予約セル（項目ごと）。終了が 00:00 の日は半開区間として含めない */
export function listRsDraftBookingCells(
  year: number,
  month: number,
  startLocal: string,
  endLocal: string,
  selectedResourceNames: readonly string[],
  boardResourceNames: readonly string[]
): RsDraftBookingCell[] {
  if (!startLocal || !endLocal || selectedResourceNames.length === 0) return [];
  const startParts = parseRsLocalDateYmd(startLocal.slice(0, 10));
  const endParts = parseRsLocalDateYmd(endLocal.slice(0, 10));
  if (!startParts || !endParts) return [];
  if (startParts.year !== year || startParts.month !== month) return [];

  const days = getRsDaysInMonth(year, month);
  const monthLast = days[days.length - 1] ?? startParts.day;
  let lastDay =
    endParts.year === year && endParts.month === month ? endParts.day : monthLast;
  // 例: 26日 22:30〜27日 00:00 → カレンダー上は 26 日だけ（終了瞬間は翌日の始まり）
  const endTime = endLocal.length >= 16 ? endLocal.slice(11, 16) : '';
  if (
    endTime === '00:00' &&
    endParts.year === year &&
    endParts.month === month &&
    lastDay > startParts.day
  ) {
    lastDay -= 1;
  }
  if (lastDay < startParts.day) lastDay = startParts.day;

  const cells: RsDraftBookingCell[] = [];
  for (const name of selectedResourceNames) {
    const resourceIndex = boardResourceNames.indexOf(name);
    if (resourceIndex < 0) continue;
    cells.push({ resourceIndex, firstDay: startParts.day, lastDay });
  }
  return cells;
}

export function toRsDateTimeLocalValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

/** `YYYY-MM-DDTHH:mm` → 表示用 `YYYY/MM/DD HH:mm`（ネイティブ datetime-local のロケール表記を避ける） */
export function formatRsDateTimeLocalDisplay(value: string): string {
  if (!value || value.length < 16) return value;
  return `${value.slice(0, 4)}/${value.slice(5, 7)}/${value.slice(8, 10)} ${value.slice(11, 16)}`;
}

/** `YYYY-MM-DD` → `YYYY/MM/DD` */
export function formatRsLocalDateDisplay(date: string): string {
  if (!date || date.length < 10) return date;
  return `${date.slice(0, 4)}/${date.slice(5, 7)}/${date.slice(8, 10)}`;
}

export function formatRsLocalDateYmd(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseRsLocalDateYmd(
  date: string
): { year: number; month: number; day: number } | null {
  if (!date || date.length < 10) return null;
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** ローカル日時が閲覧中の年月に含まれるときの「日」。それ以外は null */
export function getRsSelectedDayInMonth(
  localDateTime: string,
  year: number,
  month: number
): number | null {
  const parts = parseRsLocalDateYmd(localDateTime.slice(0, 10));
  if (!parts) return null;
  if (parts.year === year && parts.month === month) return parts.day;
  return null;
}

export function splitRsLocalDateTime(value: string): { date: string; time: string } {
  if (!value || value.length < 16) return { date: '', time: '' };
  return { date: value.slice(0, 10), time: value.slice(11, 16) };
}

export function joinRsLocalDateTime(date: string, time: string): string {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

/** min 未満なら min のスロットへ寄せる */
export function clampRsLocalDateTimeToMin(value: string, min?: string): string {
  const slotted = ceilRsLocalDateTimeToBookingSlot(value);
  if (!min) return slotted;
  const minSlotted = ceilRsLocalDateTimeToBookingSlot(min);
  return slotted < minSlotted ? minSlotted : slotted;
}

/** 日付ポップオーバーを視野内に収める（下に足りなければ上へ） */
export function resolveRsDatePopoverCoords(
  anchor: { top: number; left: number; bottom: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 8,
  gap = 4,
  /** 固定登録バーなど、視野下端を食う UI の高さ */
  bottomInset = 0
): { top: number; left: number } {
  const usableBottom = viewport.height - Math.max(0, bottomInset) - margin;
  const left = Math.min(
    Math.max(margin, anchor.left),
    Math.max(margin, viewport.width - size.width - margin)
  );
  let top = anchor.bottom + gap;
  if (top + size.height > usableBottom) {
    top = Math.max(margin, anchor.top - size.height - gap);
  }
  // 上に開いても下端ガードを超える場合は上端を押し上げる
  if (top + size.height > usableBottom) {
    top = Math.max(margin, usableBottom - size.height);
  }
  return { top, left };
}

/** 1日分の予約スロット（例: 00:00 … 23:30） */
export function listRsBookingTimeSlots(stepMinutes: number = RS_BOOKING_SLOT_MINUTES): string[] {
  const step = Math.max(1, Math.floor(stepMinutes));
  const out: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mm = String(minutes % 60).padStart(2, '0');
    out.push(`${hh}:${mm}`);
  }
  return out;
}

export const RS_BOOKING_TIME_SLOTS = listRsBookingTimeSlots();

/** 同日の終了候補など、min 以上の時刻だけ残す（`HH:mm` は辞書順比較でよい） */
export function filterRsTimeSlotsAtOrAfter(slots: readonly string[], minTime: string): string[] {
  if (!minTime) return [...slots];
  return slots.filter((slot) => slot >= minTime);
}

/** `YYYY-MM-DD` に日数を足す（ローカル暦） */
export function addDaysToRsLocalDateYmd(ymd: string, dayDelta: number): string {
  const parts = parseRsLocalDateYmd(ymd);
  if (!parts) return ymd;
  const d = new Date(parts.year, parts.month - 1, parts.day + dayDelta);
  return formatRsLocalDateYmd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

export type RsBookingTimeSelectOption = {
  value: string;
  label: string;
  date: string;
  time: string;
};

/**
 * 時刻セレクト候補。終了欄で同日 min があるとき、翌日スロットを「翌HH:mm」で続けて選べる。
 * 翌日候補は min と同じ時計時刻まで（おおよそ最長 24 時間）。
 */
export function buildRsBookingTimeSelectOptions(opts: {
  selectedDate: string;
  minLocalDateTime?: string;
  overnightPrefix?: string;
  includeOvernightContinuation?: boolean;
}): RsBookingTimeSelectOption[] {
  const { selectedDate, includeOvernightContinuation = false } = opts;
  const overnightPrefix = opts.overnightPrefix ?? '翌';
  const minSlotted = opts.minLocalDateTime
    ? ceilRsLocalDateTimeToBookingSlot(opts.minLocalDateTime)
    : '';
  const minParts = splitRsLocalDateTime(minSlotted);

  const asOptions = (
    date: string,
    times: readonly string[],
    labelFor: (time: string) => string
  ): RsBookingTimeSelectOption[] =>
    times.map((time) => ({
      value: `${date}T${time}`,
      label: labelFor(time),
      date,
      time,
    }));

  if (!selectedDate) {
    return asOptions('', RS_BOOKING_TIME_SLOTS, (time) => time);
  }
  if (!minParts.date) {
    return asOptions(selectedDate, RS_BOOKING_TIME_SLOTS, (time) => time);
  }
  if (selectedDate > minParts.date) {
    return asOptions(selectedDate, RS_BOOKING_TIME_SLOTS, (time) => time);
  }
  if (selectedDate < minParts.date) {
    return asOptions(
      minParts.date,
      filterRsTimeSlotsAtOrAfter(RS_BOOKING_TIME_SLOTS, minParts.time),
      (time) => time
    );
  }

  const sameDay = filterRsTimeSlotsAtOrAfter(RS_BOOKING_TIME_SLOTS, minParts.time);
  const sameOpts = asOptions(selectedDate, sameDay, (time) => time);
  if (!includeOvernightContinuation) return sameOpts;

  const nextDate = addDaysToRsLocalDateYmd(selectedDate, 1);
  const overnightTimes = RS_BOOKING_TIME_SLOTS.filter((time) => time <= minParts.time);
  return [
    ...sameOpts,
    ...asOptions(nextDate, overnightTimes, (time) => `${overnightPrefix}${time}`),
  ];
}

/** モバイル要約／外月バナー用。同日は終了日省略、翌日またぎは短縮 */
export function formatRsLocalDateTimeRangeSummary(
  startLocal: string,
  endLocal: string,
  overnightPrefix = '翌'
): string {
  const start = formatRsDateTimeLocalDisplay(startLocal);
  const end = formatRsDateTimeLocalDisplay(endLocal);
  if (start.length >= 16 && end.length >= 16 && start.slice(0, 10) === end.slice(0, 10)) {
    return `${start}–${end.slice(11)}`;
  }
  const compact = formatRsLocalTimeRangeDisplay(startLocal, endLocal, '–', overnightPrefix);
  if (compact && !compact.includes('/')) {
    // 例: 23:00–翌00:00 → 日付付き短表記
    const startDate = formatRsLocalDateDisplay(startLocal.slice(0, 10));
    return `${startDate} ${compact}`;
  }
  return `${start} – ${end}`;
}

/** 項目列。余白は列自体が分け合い、ヘッダだけが空帯に伸びない */
export const RS_CALENDAR_RESOURCE_COL = 'minmax(6.5rem, 1fr)';

/** カレンダーグリッド列定義（所有者は末尾に項目編集列あり） */
export function rsCalendarGridColumns(resourceCount: number, isBoardOwner: boolean): CSSProperties {
  const n = Math.max(0, resourceCount);
  const resourceCols = Array.from({ length: n }, () => RS_CALENDAR_RESOURCE_COL).join(' ');
  return {
    gridTemplateColumns: isBoardOwner
      ? `2.5rem 2.5rem ${resourceCols} 2.75rem`
      : `2.5rem 2.5rem ${resourceCols}`,
    width: '100%',
    minWidth: 'max-content',
  };
}

/** 直前の終了から次の枠へ（登録成功後・既存予約クリック直後） */
export function createNextRsBookingRangeAfterCreate(
  endLocal: string,
  durationMs: number = RS_DEFAULT_BOOKING_DURATION_MS
): { start: string; end: string } {
  const start = ceilRsLocalDateTimeToBookingSlot(endLocal);
  const span = Math.max(durationMs, RS_MIN_BOOKING_DURATION_MS);
  return {
    start,
    end: ensureRsEndAfterStart(start, addMsToRsLocalDateTime(start, span)),
  };
}

/** API の終了時刻 ISO から、その直後のローカル枠を作る */
export function createRsBookingRangeAfterBookingEnd(
  bookingEndIso: string,
  durationMs: number = RS_DEFAULT_BOOKING_DURATION_MS
): { start: string; end: string } {
  const endDate = new Date(bookingEndIso);
  if (Number.isNaN(endDate.getTime())) {
    return createDefaultRsBookingRange();
  }
  return createNextRsBookingRangeAfterCreate(toRsDateTimeLocalValue(endDate), durationMs);
}

export function toRsApiDateTime(localValue: string): string {
  return localValue.length === 16 ? `${localValue}:00` : localValue;
}

export function rsBookingOverlapsDay(
  booking: ReservationBooking,
  viewYear: number,
  viewMonth: number,
  day: number
): boolean {
  const dayStart = new Date(viewYear, viewMonth - 1, day, 0, 0, 0, 0);
  const nextDayStart = new Date(viewYear, viewMonth - 1, day + 1, 0, 0, 0, 0);
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);
  // 半開区間 [start, end): 終了が翌日 00:00 ちょうどならその日には載せない
  return start < nextDayStart && end > dayStart;
}

/** 半開区間に近い重なり判定: A開始 < B終了 かつ A終了 > B開始 */
export function rsDateRangesOverlap(
  startAMs: number,
  endAMs: number,
  startBMs: number,
  endBMs: number
): boolean {
  return startAMs < endBMs && endAMs > startBMs;
}

export function rsLocalRangeOverlapsBooking(
  startLocal: string,
  endLocal: string,
  booking: Pick<ReservationBooking, 'startTime' | 'endTime'>
): boolean {
  const startA = new Date(startLocal).getTime();
  const endA = new Date(endLocal).getTime();
  const startB = new Date(booking.startTime).getTime();
  const endB = new Date(booking.endTime).getTime();
  if ([startA, endA, startB, endB].some((n) => Number.isNaN(n))) return false;
  return rsDateRangesOverlap(startA, endA, startB, endB);
}

/** 登録前ドラフトが既存予約と重なる項目名 */
export function listRsDraftOverlapResourceNames(
  startLocal: string,
  endLocal: string,
  selectedResourceNames: readonly string[],
  bookings: readonly Pick<ReservationBooking, 'resourceName' | 'startTime' | 'endTime'>[]
): string[] {
  if (!startLocal || !endLocal || selectedResourceNames.length === 0) return [];
  return selectedResourceNames.filter((name) =>
    bookings.some(
      (booking) =>
        booking.resourceName === name && rsLocalRangeOverlapsBooking(startLocal, endLocal, booking)
    )
  );
}

export function getRsBookingDaySpanInMonth(
  booking: ReservationBooking,
  viewYear: number,
  viewMonth: number,
  daysInMonth: number[]
): { firstDay: number; lastDay: number } | null {
  let firstDay: number | null = null;
  let lastDay: number | null = null;
  for (const day of daysInMonth) {
    if (!rsBookingOverlapsDay(booking, viewYear, viewMonth, day)) continue;
    if (firstDay === null) firstDay = day;
    lastDay = day;
  }
  if (firstDay === null || lastDay === null) return null;
  return { firstDay, lastDay };
}

export interface RsBookingSpanGroup {
  firstDay: number;
  lastDay: number;
  resourceIndex: number;
  bookings: ReservationBooking[];
}

export function createDefaultRsBookingRange(): { start: string; end: string } {
  const start = ceilRsDateToBookingSlot(new Date());
  const end = new Date(start.getTime() + RS_DEFAULT_BOOKING_DURATION_MS);
  return {
    start: toRsDateTimeLocalValue(start),
    end: toRsDateTimeLocalValue(end),
  };
}

/** 枠長（ms）。不正な範囲は既定枠 */
export function getRsBookingDurationMs(startLocal: string, endLocal: string): number {
  const start = new Date(startLocal);
  const end = new Date(endLocal);
  if (
    !Number.isNaN(start.getTime()) &&
    !Number.isNaN(end.getTime()) &&
    end.getTime() > start.getTime()
  ) {
    return end.getTime() - start.getTime();
  }
  return RS_DEFAULT_BOOKING_DURATION_MS;
}

/** フォーム表示用。不正な範囲は既定枠（分）を返す */
export function getRsBookingDurationMinutes(startLocal: string, endLocal: string): number {
  return Math.max(1, Math.round(getRsBookingDurationMs(startLocal, endLocal) / 60_000));
}

/** カレンダー上の日付クリック用。時刻は preferStart、枠長は preferEnd から引き継ぐ。 */
export function createRsBookingRangeForCalendarDay(
  year: number,
  month: number,
  day: number,
  preferStart?: string,
  preferEnd?: string
): { start: string; end: string } {
  let hours = 9;
  let minutes = 0;
  if (preferStart && preferStart.length >= 16) {
    const parsedH = Number(preferStart.slice(11, 13));
    const parsedM = Number(preferStart.slice(14, 16));
    if (!Number.isNaN(parsedH) && !Number.isNaN(parsedM)) {
      hours = parsedH;
      minutes = parsedM;
    }
  }
  const start = new Date(year, month - 1, day, hours, minutes, 0, 0);
  const durationMs =
    preferStart && preferEnd
      ? getRsBookingDurationMs(preferStart, preferEnd)
      : RS_DEFAULT_BOOKING_DURATION_MS;
  const end = new Date(start.getTime() + durationMs);
  return {
    start: toRsDateTimeLocalValue(start),
    end: toRsDateTimeLocalValue(end),
  };
}

export function addMsToRsLocalDateTime(localValue: string, ms: number): string {
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return localValue;
  return toRsDateTimeLocalValue(new Date(d.getTime() + ms));
}

/** 終了が開始以下なら、開始 + 最小枠（既定 30 分）後に補正する */
export function ensureRsEndAfterStart(startLocal: string, endLocal: string): string {
  const start = new Date(startLocal);
  const end = new Date(endLocal);
  if (Number.isNaN(start.getTime())) return endLocal;
  if (Number.isNaN(end.getTime()) || end <= start) {
    return addMsToRsLocalDateTime(startLocal, RS_MIN_BOOKING_DURATION_MS);
  }
  return endLocal;
}

/** 開始を動かしても枠の長さ（終了−開始）を維持する */
export function moveRsBookingStartKeepingDuration(
  startLocal: string,
  endLocal: string,
  nextStartLocal: string
): { start: string; end: string } {
  const nextStart = new Date(nextStartLocal);
  if (Number.isNaN(nextStart.getTime())) {
    return { start: startLocal, end: endLocal };
  }
  const durationMs = getRsBookingDurationMs(startLocal, endLocal);
  const start = toRsDateTimeLocalValue(nextStart);
  return {
    start,
    end: addMsToRsLocalDateTime(start, durationMs),
  };
}

/** 開始・終了を同じだけずらす（±30 分ボタン用） */
export function shiftRsBookingRangeByMs(
  startLocal: string,
  endLocal: string,
  ms: number
): { start: string; end: string } {
  return moveRsBookingStartKeepingDuration(
    startLocal,
    endLocal,
    addMsToRsLocalDateTime(startLocal, ms)
  );
}

/** 開始を「いま」の次スロットへ。枠長は維持（OS ピッカーを開かずに直近枠へ） */
export function moveRsBookingStartToNowKeepingDuration(
  startLocal: string,
  endLocal: string
): { start: string; end: string } {
  return moveRsBookingStartKeepingDuration(
    startLocal,
    endLocal,
    toRsDateTimeLocalValue(ceilRsDateToBookingSlot(new Date()))
  );
}

/** 終了だけ ±ms。開始以下なら最小枠へ補正 */
export function nudgeRsBookingEnd(
  startLocal: string,
  endLocal: string,
  deltaMs: number
): string {
  return ensureRsEndAfterStart(startLocal, addMsToRsLocalDateTime(endLocal, deltaMs));
}

export function rsResourceNamesEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((name, index) => name === b[index]);
}

const DEFAULT_RS_RESOURCE_NAME = (n: number) => `項目${n}`;

export function suggestNextRsResourceName(
  existing: string[],
  formatName: (n: number) => string = DEFAULT_RS_RESOURCE_NAME
): string {
  const nameFor = typeof formatName === 'function' ? formatName : DEFAULT_RS_RESOURCE_NAME;
  let n = 1;
  while (existing.includes(nameFor(n))) {
    n += 1;
  }
  return nameFor(n);
}

export function rsBookingDisplayName(booking: ReservationBooking): string {
  if (booking.displayName) return booking.displayName;
  if (booking.user) {
    const parts = [booking.user.lastName, booking.user.firstName].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
    return booking.user.username;
  }
  return '-';
}

/** デモ投入用の内部印。画面の備考としては出さない */
export const RS_DEMO_NOTE_SENTINEL = '__rs_demo__';

export function visibleRsBookingNotes(notes: string | null | undefined): string {
  const text = notes?.trim() ?? '';
  if (!text || text === RS_DEMO_NOTE_SENTINEL) return '';
  return text;
}

export const RS_CREATE_BOOKING_FORM_ERRORS = {
  endBeforeStart: {
    key: 'reservation.board.endBeforeStart',
    defaultValue: '終了日時は開始日時より後にしてください。',
  },
  resourceRequired: {
    key: 'reservation.board.resourceRequired',
    defaultValue: '項目を1つ以上選択してください。',
  },
  displayNameRequired: {
    key: 'reservation.board.displayNameRequired',
    defaultValue: '所属・名前は必須です。',
  },
} as const;

export function getRsCreateBookingFormErrorKey(input: {
  startDateTime: string;
  endDateTime: string;
  selectedResourceNames: string[];
  displayName: string;
}): (typeof RS_CREATE_BOOKING_FORM_ERRORS)[keyof typeof RS_CREATE_BOOKING_FORM_ERRORS] | null {
  if (new Date(input.startDateTime) >= new Date(input.endDateTime)) {
    return RS_CREATE_BOOKING_FORM_ERRORS.endBeforeStart;
  }
  if (input.selectedResourceNames.length === 0) {
    return RS_CREATE_BOOKING_FORM_ERRORS.resourceRequired;
  }
  if (!input.displayName.trim()) {
    return RS_CREATE_BOOKING_FORM_ERRORS.displayNameRequired;
  }
  return null;
}

export function getRsDaysInMonth(year: number, month: number): number[] {
  const count = new Date(year, month, 0).getDate();
  return Array.from({ length: count }, (_, i) => i + 1);
}

export function shiftRsCalendarMonth(
  year: number,
  month: number,
  delta: number
): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function buildRsBookingSpanGroups(
  bookings: ReservationBooking[],
  resourceNames: string[],
  year: number,
  month: number,
  daysInMonth: number[]
): RsBookingSpanGroup[] {
  const groupMap = new Map<string, RsBookingSpanGroup>();
  for (const booking of bookings) {
    const span = getRsBookingDaySpanInMonth(booking, year, month, daysInMonth);
    if (!span) continue;
    const resourceIndex = resourceNames.indexOf(booking.resourceName);
    if (resourceIndex < 0) continue;
    const key = `${resourceIndex}:${span.firstDay}:${span.lastDay}`;
    const existing = groupMap.get(key);
    if (existing) {
      existing.bookings.push(booking);
    } else {
      groupMap.set(key, { ...span, resourceIndex, bookings: [booking] });
    }
  }
  for (const group of groupMap.values()) {
    group.bookings.sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  }
  return Array.from(groupMap.values());
}
